/**
 * Resultados de ENTIDADES dentro de la paleta ⌘K (facturas, empleados, proyectos,
 * clientes y proveedores).
 *
 * Hasta ahora ⌘K solo navegaba entre pantallas: quien tecleaba el nombre de un
 * cliente no encontraba nada. Busca en `GET /api/search`, que ignora acentos
 * («Jimenez» encuentra «Jiménez») y solo devuelve los tipos cuyo listado puede
 * leer el usuario: el permiso lo decide el servidor, no esta pantalla.
 *
 * Solo se piden los tipos que tienen a dónde ir. Contactos de CRM y oportunidades
 * se quedan fuera hasta que sus pantallas acepten abrir una ficha por URL: un
 * resultado que lleva a un listado genérico enseña a no volver a usar ⌘K.
 */
import { useEffect, useState } from 'react';
import { Command } from 'cmdk';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BookUser, Briefcase, FileText, FolderKanban, Users } from 'lucide-react';
import { apiFetch, unwrapData } from '@/api/apiFetch';
import { useApp } from '@/components/hooks/useApp';
import { createPageUrl } from '@/components/utils';

const DEBOUNCE_MS = 250;
const LIMITE = 8;
export const MIN_CARACTERES = 2;

/** A dónde lleva cada tipo. Un tipo sin entrada aquí no se pide al servidor. */
export const DESTINOS = {
  factura: { icon: FileText, url: (r) => createPageUrl('FacturaDetalle', { id: r.id }) },
  empleado: { icon: Users, url: (r) => createPageUrl('EmployeeDetail', { id: r.id }) },
  proyecto: { icon: FolderKanban, url: (r) => createPageUrl('ProjectDetail', { id: r.id }) },
  // Las fichas 360 a las que ya navega el directorio de contactos al pulsar un nombre.
  cliente: { icon: BookUser, url: (r) => `/clientes/${encodeURIComponent(r.id)}` },
  proveedor: { icon: Briefcase, url: (r) => `/proveedores/${encodeURIComponent(r.id)}` },
};

export function useBusquedaGlobal(busqueda) {
  const { company } = useApp();
  const cid = company?.id;
  const raw = (busqueda || '').trim();

  // Una request por término estable, no por tecla.
  const [estable, setEstable] = useState(raw);
  useEffect(() => {
    const id = setTimeout(() => setEstable(raw), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [raw]);

  const activa = !!cid && estable.length >= MIN_CARACTERES;
  const { data, isLoading, isFetching, isError } = useQuery({
    // eslint-disable-next-line local/no-literal-querykey -- caché por término de la paleta, aislada por company; no la invalida ninguna mutación
    queryKey: ['global-search', cid, estable],
    queryFn: async () => {
      const params = new URLSearchParams({ q: estable, tipos: Object.keys(DESTINOS).join(','), limit: String(LIMITE) });
      return unwrapData(await apiFetch(`/api/search?${params.toString()}`));
    },
    enabled: activa,
    staleTime: 60 * 1000,
  });

  return {
    resultados: activa ? (data || []).filter((r) => DESTINOS[r.tipo]) : [],
    cargando: activa ? (isLoading || isFetching || raw !== estable) : raw.length >= MIN_CARACTERES,
    error: activa && isError,
  };
}

const HEADING = `[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2
  [&_[cmdk-group-heading]]:text-micro [&_[cmdk-group-heading]]:font-semibold
  [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider
  [&_[cmdk-group-heading]]:text-muted-foreground px-2 pb-1`;

export default function ResultadosBusqueda({ busqueda, onSelect }) {
  const { t } = useTranslation();
  const { resultados, cargando, error } = useBusquedaGlobal(busqueda);

  if (error) {
    return (
      <Command.Group heading={t('command.company_records')} className={HEADING}>
        {/* `keywords` con el término: si no, el filtro de cmdk esconde el aviso. */}
        <Command.Item disabled value="__busqueda_error" keywords={[busqueda]} className="px-3 py-2.5 text-sm text-destructive">
          {t('command.search_error')}
        </Command.Item>
      </Command.Group>
    );
  }
  if (cargando && resultados.length === 0) {
    return <Command.Item disabled value={`__buscando_${busqueda}`} className="px-5 py-2 text-sm text-muted-foreground">{t('command.searching')}</Command.Item>;
  }
  if (resultados.length === 0) return null;

  return (
    <Command.Group heading={t('command.company_records')} className={HEADING}>
      {resultados.map((r) => {
        const { icon: Icon, url } = DESTINOS[r.tipo];
        return (
          <Command.Item
            key={`${r.tipo}:${r.id}`}
            value={`${r.tipo}:${r.id}:${r.nombre}`}
            // El servidor ya ha filtrado por el término; sin esto, cmdk vuelve a
            // filtrar por su cuenta y descarta aciertos sin acentos.
            keywords={[busqueda]}
            onSelect={() => onSelect(url(r))}
            className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-foreground outline-none cursor-pointer select-none aria-selected:bg-accent aria-selected:text-accent-foreground"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
              <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{r.nombre}</span>
              {r.detalle ? <span className="block truncate text-xs text-muted-foreground">{r.detalle}</span> : null}
            </span>
            <span className="shrink-0 text-micro text-muted-foreground">{t(`command.tipo_${r.tipo}`)}</span>
          </Command.Item>
        );
      })}
    </Command.Group>
  );
}
